import { useQuery } from '@tanstack/react-query'
import apiClient from '@/lib/api-client'

/**
 * Subscription plan information
 */
export interface Plan {
  id: string
  slug: string
  name: string
  price: string
  price_formatted: string
  features?: Record<string, boolean>
  quotas?: Record<string, number>
}

/**
 * User subscription details from API
 */
export interface Subscription {
  id: string
  status: string
  is_active: boolean
  is_in_trial: boolean
  current_period_start: string
  current_period_end: string
  trial_ends_at: string | null
  days_remaining: number
  cancelled_at: string | null
  plan: Plan | null
}

/**
 * Key feature flags that gate specific UI elements
 * These are the features that control visibility of UI elements
 */
export const FEATURE_GATES = {
  // Analytics - professional+ only
  ANALYTICS: 'profile.analytics',
  // Dynamic pricing - growth+ only
  DYNAMIC_PRICING: 'pricing.dynamic',
  // Email capture - unlimited only
  EMAIL_CAPTURE: 'marketing.email_capture',
  // Custom domain - professional+ only
  CUSTOM_DOMAIN: 'profile.custom_domain',
  // Remove branding - professional+ only
  REMOVE_BRANDING: 'profile.remove_branding',
  // Client galleries - professional+ only
  CLIENT_GALLERIES: 'albums.client_galleries',
  // QR code branding - professional+ only
  QR_CODE_BRANDED: 'marketing.qr_code_branded',
  // Priority support - unlimited only
  PRIORITY_SUPPORT: 'support.priority',
} as const

export type FeatureKey = keyof Record<string, boolean> | (typeof FEATURE_GATES)[keyof typeof FEATURE_GATES]

interface SubscriptionResponse {
  data: Subscription | null
}

async function fetchCurrentSubscription(): Promise<Subscription | null> {
  const response = await apiClient.get<SubscriptionResponse>('/api/v1/subscriptions/current')
  return response.data.data
}

export interface UseSubscriptionReturn {
  subscription: Subscription | null
  plan: Plan | null
  planSlug: string | null
  isLoading: boolean
  isError: boolean
  error: Error | null
  hasFeature: (feature: FeatureKey) => boolean
  isActive: boolean
  isTrialing: boolean
}

/**
 * Hook to access the current user's subscription and plan features.
 * Provides a simple interface to check feature availability.
 * 
 * @example
 * ```tsx
 * const { hasFeature, plan } = useSubscription()
 * 
 * if (hasFeature('profile.analytics')) {
 *   // Show analytics
 * }
 * ```
 */
export function useSubscription(): UseSubscriptionReturn {
  const { data: subscription, isLoading, isError, error } = useQuery({
    queryKey: ['current-subscription'],
    queryFn: fetchCurrentSubscription,
    staleTime: 1000 * 60 * 5, // 5 minutes
    retry: false,
  })

  const plan = subscription?.plan ?? null
  const planSlug = plan?.slug ?? null
  const planFeatures = plan?.features ?? {}

  /**
   * Check if the user has a specific feature enabled.
   * Uses features from the API response when available.
   * Falls back to plan-slug based check if features are not available.
   */
  const hasFeature = (feature: FeatureKey): boolean => {
    if (!subscription) {
      return false
    }

    // Use actual features from API if available
    if (Object.keys(planFeatures).length > 0) {
      return planFeatures[feature] ?? false
    }

    // Fallback: check against plan slug
    return checkFeatureByPlanSlug(feature, planSlug)
  }

  return {
    subscription: subscription ?? null,
    plan,
    planSlug,
    isLoading,
    isError,
    error: error as Error | null,
    hasFeature,
    isActive: subscription?.is_active ?? false,
    isTrialing: subscription?.is_in_trial ?? false,
  }
}

/**
 * Check if a feature is enabled based on plan slug.
 * This is a fallback for when API doesn't return features directly.
 * 
 * Feature availability by plan:
 * - starter: booking.enabled only
 * - growth: booking.enabled, pricing.dynamic
 * - professional: profile.analytics, profile.custom_domain, profile.remove_branding,
 *                pricing.dynamic, booking.enabled, booking.client_galleries,
 *                services.unlimited, albums.client_galleries, marketing.qr_code_branded
 * - unlimited: all features
 */
function checkFeatureByPlanSlug(feature: FeatureKey, planSlug: string | null): boolean {
  if (!planSlug) {
    return false
  }

  const featureMap: Record<string, string[]> = {
    // Profile features
    'profile.custom_domain': ['professional', 'unlimited'],
    'profile.remove_branding': ['professional', 'unlimited'],
    'profile.analytics': ['professional', 'unlimited'],
    
    // Gallery features
    'gallery.unlimited_images': ['unlimited'],
    
    // Service features
    'services.unlimited': ['professional', 'unlimited'],
    
    // Album features
    'albums.client_galleries': ['professional', 'unlimited'],
    
    // Booking features
    'booking.client_galleries': ['professional', 'unlimited'],
    
    // Pricing features
    'pricing.dynamic': ['growth', 'professional', 'unlimited'],
    
    // Marketing features
    'marketing.email_capture': ['unlimited'],
    'marketing.qr_code_branded': ['professional', 'unlimited'],
    
    // Support features
    'support.priority': ['unlimited'],
    
    // Base features available to all plans
    'booking.enabled': ['free', 'starter', 'growth', 'professional', 'unlimited'],
    'booking.client_verification': ['starter', 'growth', 'professional', 'unlimited'],
    'gallery.max_images': ['starter', 'growth', 'professional', 'unlimited'],
    'services.max': ['starter', 'growth', 'professional', 'unlimited'],
    'albums.max': ['starter', 'growth', 'professional', 'unlimited'],
    'booking.max_bookings_per_month': ['starter', 'growth', 'professional', 'unlimited'],
  }

  const allowedPlans = featureMap[feature]
  if (!allowedPlans) {
    // Unknown feature, default to false for safety
    return false
  }

  return allowedPlans.includes(planSlug)
}

export default useSubscription
